import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/ui/combobox";
import { Plus, Clock, CheckCircle, XCircle, AlertCircle, Eye, Download, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import DatePicker from "@/components/DatePicker";

// Non-billable tasks
const NON_BILLABLE_TASKS = [
  "Practise",
  "None/general",
  "Training",
  "Meeting",
  "Ideal",
  "Document Preparation",
  "Training Support"
];

// Billable tasks (all tasks except non-billable)
const BILLABLE_TASKS_PROJECTS = [
  "Modeling",
  "Model checking",
  "Detailing",
  "Detailsheet checking",
  "Erection",
  "Erection checking",
  "RFI",
  "Coordination",
  "Upload",
  "Job study",
  "Back drafting",
  "Back checking",
  "Planning",
  "Change order"
];

// For backward compatibility
const TASK_OPTIONS_PROJECTS = [...BILLABLE_TASKS_PROJECTS, ...NON_BILLABLE_TASKS];

const TASK_OPTIONS_ESTIMATION = [
  "Folder Preparation",
  "Drawing Segregation",
  "Drawing Log Preparation",
  "Drawing Marking and Counting",
  "Scope & Note Preparation",
  "WBS Preparation",
  "Estimation Checking",
  "Rebidding",
  "Awarded Project Data Sharing",
  "Scope Clarification- Internal & External",
  "Proposal Preparation",
  "Bid Preparation"
];

const TYPE_OPTIONS_PROJECTS = ["Base", "CCN", "Rework"];

export default function Timesheets({ user }) {
  const [timesheets, setTimesheets] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [rejectedTimesheets, setRejectedTimesheets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [estimations, setEstimations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [descriptionModal, setDescriptionModal] = useState({ open: false, text: "", date: "" });
  const [pullLoading, setPullLoading] = useState(false);
  const [conflictModal, setConflictModal] = useState({ open: false, conflicts: [], resolutions: {}, period: "" });
  const [earnedHours, setEarnedHours] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [projectHoursSummary, setProjectHoursSummary] = useState(null);
  const [selectedEstimationVersions, setSelectedEstimationVersions] = useState([]);
  
  // Pending Approvals Filters
  const [approvalFilters, setApprovalFilters] = useState({
    userId: "all",
    dateFrom: "",
    dateTo: "",
    referenceProject: "all"
  });
  
  // Detect user department
  const userDepartment = user?.department || "Projects";
  
  const [editFormData, setEditFormData] = useState({
    is_billable: true,  // Track billable status for edit
    department: userDepartment,
    reference_id: "",
    project_id: "",
    estimation_id: "",
    branch: "",  // Branch for multi-branch projects
    task: "",
    type: userDepartment === "Estimation" ? "" : "Base",
    date: "",
    actual_hours: "",
    work_description: ""
  });
  const [formData, setFormData] = useState({
    is_billable: true,  // Default to billable
    department: userDepartment,
    reference_id: "",
    project_id: "",
    estimation_id: "",
    branch: "",  // Branch for multi-branch projects
    task: "",
    type: userDepartment === "Estimation" ? "" : "Base",
    date: new Date().toISOString().split('T')[0],
    actual_hours: "",
    work_description: ""
  });

  useEffect(() => {
    fetchTimesheets();
    fetchPendingApprovals();
    fetchRejectedTimesheets();
    fetchProjects();
    fetchUsers();
    // Always fetch estimations so managers can review estimation timesheets
    fetchEstimations();
  }, []);

  const fetchTimesheets = async () => {
    try {
      const response = await api.get("/timesheets");
      setTimesheets(sortByDateDesc(response.data));
    } catch (error) {
      toast.error("Failed to fetch timesheets");
    } finally {
      setLoading(false);
    }
  };

  // Sort timesheet rows descending by date (latest first), tie-broken by updated_at.
  const sortByDateDesc = (rows) =>
    [...(rows || [])].sort((a, b) => {
      const da = a.date || "";
      const db = b.date || "";
      if (db !== da) return db.localeCompare(da);
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });

  const fetchPendingApprovals = async () => {
    try {
      const response = await api.get("/timesheets/pending-approvals");
      setPendingApprovals(sortByDateDesc(response.data));
    } catch (error) {
      console.error("Failed to fetch pending approvals");
    }
  };

  const fetchRejectedTimesheets = async () => {
    try {
      const response = await api.get("/timesheets/my-rejected");
      setRejectedTimesheets(sortByDateDesc(response.data));
    } catch (error) {
      console.error("Failed to fetch rejected timesheets");
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get("/projects");
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  const fetchEstimations = async () => {
    try {
      const response = await api.get("/estimations");
      setEstimations(response.data);
    } catch (error) {
      console.error("Failed to fetch estimations");
    }
  };

  const handleEstimationChange = async (estimationId) => {
    setFormData({ ...formData, reference_id: estimationId, estimation_id: estimationId, type: "" });
    
    // Fetch all versions of this estimation
    try {
      const response = await api.get(`/estimations/${estimationId}/versions`);
      setSelectedEstimationVersions(response.data.map(est => est.version));
    } catch (error) {
      console.error("Failed to fetch estimation versions");
      // If versions endpoint doesn't exist, just use the selected estimation's version
      const estimation = estimations.find(e => e.id === estimationId);
      if (estimation) {
        setSelectedEstimationVersions([estimation.version]);
      }
    }
  };

  const handlePullWorkLogs = async () => {
    setPullLoading(true);
    try {
      const response = await api.post("/timesheets/pull-from-work-logs", {});
      const { message, pulled_count, already_pulled_count, conflicts, start_date, end_date, grace_active, grace_reason } = response.data;
      const rangeLabel = start_date && end_date ? ` (${start_date} → ${end_date})` : "";
      const graceSuffix = grace_active && grace_reason ? ` ${grace_reason}` : "";
      if (pulled_count > 0 || already_pulled_count > 0) {
        toast.success(`${message}${rangeLabel}${graceSuffix}`);
      } else if (!conflicts || conflicts.length === 0) {
        toast.info(`${message || "No work logs found in this period"}${rangeLabel}${graceSuffix}`);
      }
      if (conflicts && conflicts.length > 0) {
        const resolutions = {};
        conflicts.forEach((c) => { resolutions[c.log_id] = "skip"; });
        setConflictModal({ open: true, conflicts, resolutions, period: `${start_date} → ${end_date}` });
      }
      if (pulled_count > 0) {
        fetchTimesheets();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pull work logs");
    } finally {
      setPullLoading(false);
    }
  };

  const handleResolveConflicts = async () => {
    const resolutions = conflictModal.conflicts.map((c) => ({
      log_id: c.log_id,
      action: conflictModal.resolutions[c.log_id] || "skip",
      existing_timesheet_id: c.existing_timesheet_id,
    }));
    setPullLoading(true);
    try {
      const res = await api.post("/timesheets/pull-from-work-logs/resolve", { resolutions });
      toast.success(res.data?.message || "Resolutions applied");
      setConflictModal({ open: false, conflicts: [], resolutions: {}, period: "" });
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply resolutions");
    } finally {
      setPullLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate billable requirements
    if (formData.is_billable) {
      // For billable entries, project/estimation is required
      if (!formData.reference_id) {
        toast.error(`Please select a ${userDepartment === "Projects" ? "project" : "estimation"}`);
        return;
      }
      
      // Validate branch selection for multi-branch projects (Projects only)
      if (userDepartment === "Projects") {
        const selectedProject = projects.find(p => p.id === formData.reference_id);
        const isMultiBranch = selectedProject && Array.isArray(selectedProject.branch) && selectedProject.branch.length > 1;
        
        if (isMultiBranch && !formData.branch) {
          toast.error("Please select a branch for this multi-branch project");
          return;
        }
      }
      
      // Type is required for billable entries
      if (!formData.type) {
        toast.error(`Please select ${userDepartment === "Estimation" ? "version" : "type"}`);
        return;
      }
    }
    
    try {
      const payload = {
        is_billable: formData.is_billable,
        department: formData.department,
        reference_type: formData.is_billable ? (formData.department === "Projects" ? "project" : "estimation") : null,
        reference_id: formData.is_billable ? formData.reference_id : null,
        project_id: formData.is_billable && formData.department === "Projects" ? formData.reference_id : null,
        estimation_id: formData.is_billable && formData.department === "Estimation" ? formData.reference_id : null,
        branch: formData.is_billable ? (formData.branch || null) : null,
        task: formData.task,
        type: formData.is_billable ? formData.type : null,
        date: new Date(formData.date).toISOString(),
        actual_hours: parseFloat(formData.actual_hours),
        work_description: formData.work_description || null
      };
      await api.post("/timesheets", payload);
      toast.success("Timesheet entry created!");
      setDialogOpen(false);
      fetchTimesheets();
      fetchPendingApprovals();  // Refresh pending approvals
      setFormData({
        is_billable: true,
        department: userDepartment,
        reference_id: "",
        project_id: "",
        estimation_id: "",
        branch: "",
        task: "",
        type: userDepartment === "Estimation" ? "" : "Base",
        date: new Date().toISOString().split('T')[0],
        actual_hours: "",
        work_description: ""
      });
      setSelectedEstimationVersions([]);
    } catch (error) {
      toast.error("Failed to create timesheet entry");
    }
  };

  const handleEditTimesheet = (timesheet) => {
    setEditingTimesheet(timesheet);
    setEditFormData({
      is_billable: timesheet.is_billable !== false,  // Default to true if not set
      department: timesheet.department || "Projects",
      project_id: timesheet.project_id,
      reference_id: timesheet.reference_id,
      branch: timesheet.branch || "",
      task: timesheet.task,
      type: timesheet.type || "",
      date: new Date(timesheet.date).toISOString().split('T')[0],
      actual_hours: timesheet.actual_hours.toString(),
      work_description: timesheet.work_description || ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTimesheet = async (e) => {
    e.preventDefault();
    
    // For non-billable, skip project/branch validation
    if (editFormData.is_billable !== false) {
      // Validate branch selection for multi-branch projects
      const selectedProject = projects.find(p => p.id === editFormData.project_id);
      const isMultiBranch = selectedProject && Array.isArray(selectedProject.branch) && selectedProject.branch.length > 1;
      
      if (isMultiBranch && !editFormData.branch) {
        toast.error("Please select a branch for this multi-branch project");
        return;
      }
    }
    
    try {
      let payload;
      
      if (editFormData.is_billable === false) {
        // Non-billable: only update basic fields
        payload = {
          task: editFormData.task,
          date: new Date(editFormData.date).toISOString(),
          actual_hours: parseFloat(editFormData.actual_hours),
          work_description: editFormData.work_description || null
        };
      } else {
        // Billable: include project/branch/type and reference_id
        payload = {
          project_id: editFormData.project_id,
          reference_id: editFormData.project_id,  // Keep reference_id in sync with project_id
          branch: editFormData.branch || null,
          task: editFormData.task,
          type: editFormData.type,
          date: new Date(editFormData.date).toISOString(),
          actual_hours: parseFloat(editFormData.actual_hours),
          work_description: editFormData.work_description || null
        };
      }
      
      await api.patch(`/timesheets/${editingTimesheet.id}`, payload);
      toast.success("Timesheet updated!");
      setEditDialogOpen(false);
      setEditingTimesheet(null);
      fetchTimesheets();
      fetchRejectedTimesheets();  // Refresh rejected timesheets list
      fetchPendingApprovals();  // Refresh pending approvals
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update timesheet");
    }
  };

  const handleSubmitTimesheet = async (timesheetId) => {
    if (!window.confirm("Submit this timesheet for manager approval?")) {
      return;
    }
    
    try {
      await api.patch(`/timesheets/${timesheetId}/submit`);
      toast.success("Timesheet submitted for approval!");
      fetchTimesheets();
      fetchPendingApprovals();
      fetchRejectedTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit timesheet");
    }
  };

  const handleDeleteTimesheet = async (timesheetId) => {
    if (!window.confirm("Are you sure you want to delete this draft timesheet? This action cannot be undone.")) {
      return;
    }
    
    try {
      await api.delete(`/timesheets/${timesheetId}`);
      toast.success("Timesheet deleted successfully!");
      fetchTimesheets();
      fetchPendingApprovals();  // Refresh in case deleted timesheet was pending
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete timesheet");
    }
  };

  const handleApprove = async () => {
    // For non-billable, earned hours is always 0 (set by backend)
    // For billable, use the input value
    const hoursToApprove = selectedTimesheet.is_billable === false 
      ? 0 
      : parseFloat(earnedHours);
    
    // Only validate earned hours for billable timesheets
    if (selectedTimesheet.is_billable !== false && !hoursToApprove && hoursToApprove !== 0) {
      toast.error("Please enter earned hours");
      return;
    }
    
    try {
      await api.patch(`/timesheets/${selectedTimesheet.id}/approve`, {
        earned_hours: hoursToApprove
      });
      toast.success("Timesheet approved!");
      setApprovalDialogOpen(false);
      setSelectedTimesheet(null);
      setEarnedHours("");
      fetchTimesheets();
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve timesheet");
    }
  };

  const handleReject = async () => {
    if (!rejectionReason) {
      toast.error("Please enter rejection reason");
      return;
    }
    
    try {
      await api.patch(`/timesheets/${selectedTimesheet.id}/reject`, {
        rejection_reason: rejectionReason
      });
      toast.success("Timesheet rejected - sent back to user for revision");
      setApprovalDialogOpen(false);
      setSelectedTimesheet(null);
      setRejectionReason("");
      fetchTimesheets();
      fetchPendingApprovals();
      fetchRejectedTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject timesheet");
    }
  };

  const openApprovalDialog = async (timesheet) => {
    setSelectedTimesheet(timesheet);
    setEarnedHours(timesheet.actual_hours.toString());
    setApprovalDialogOpen(true);
    
    // Fetch project hours summary only for billable Projects department timesheets
    // Non-billable timesheets don't have project allocation
    const dept = timesheet.department || "Projects";
    const isBillable = timesheet.is_billable !== false;
    
    if (dept === "Projects" && isBillable && timesheet.project_id) {
      try {
        const response = await api.get(`/timesheets/${timesheet.id}/project-hours-summary`);
        setProjectHoursSummary(response.data);
      } catch (error) {
        console.error("Failed to fetch project hours summary:", error);
        toast.error("Failed to load project hours information");
      }
    } else {
      // Clear project hours summary for non-billable or estimation timesheets
      setProjectHoursSummary(null);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? `${project.project_number} - ${project.name}` : "Unknown";
  };

  const getEstimationName = (estimationId) => {
    const estimation = estimations.find(e => e.id === estimationId);
    return estimation ? `${estimation.estimation_number} - ${estimation.project_name}` : "Unknown";
  };

  const getReferenceName = (timesheet) => {
    const dept = timesheet.department || "Projects";
    const refId = timesheet.reference_id || timesheet.project_id || timesheet.estimation_id;
    
    if (dept === "Estimation") {
      return getEstimationName(refId);
    } else {
      return getProjectName(refId);
    }
  };

  const getProjectNumber = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.project_number : "Unknown";
  };

  const getEstimationNumber = (estimationId) => {
    const estimation = estimations.find(e => e.id === estimationId);
    return estimation ? estimation.estimation_number : "Unknown";
  };

  const getUserName = (userId, timesheet = null) => {
    // First check if timesheet has denormalized user_name
    if (timesheet && timesheet.user_name) {
      return timesheet.user_name;
    }
    // Fall back to looking up in users array
    const userObj = users.find(u => u.id === userId);
    return userObj ? `${userObj.first_name} ${userObj.last_name || ''}`.trim() : "Unknown";
  };

  const getStatusBadge = (status) => {
    const colors = {
      Draft: "bg-gray-400",
      Pending: "bg-yellow-500",
      Approved: "bg-green-500",
      Rejected: "bg-red-500",
      "Rejected - Resubmit": "bg-orange-500"
    };
    return <Badge className={colors[status] || "bg-gray-500"}>{status}</Badge>;
  };

  const getTotalActualHours = () => {
    return timesheets.reduce((sum, ts) => sum + (ts.actual_hours || 0), 0).toFixed(2);
  };

  const getTotalEarnedHours = () => {
    return timesheets.reduce((sum, ts) => sum + (ts.earned_hours || 0), 0).toFixed(2);
  };

  // Filter pending approvals based on selected filters
  const filteredPendingApprovals = pendingApprovals.filter(timesheet => {
    // User filter
    if (approvalFilters.userId !== "all" && timesheet.user_id !== approvalFilters.userId) {
      return false;
    }
    
    // Date range filter
    if (approvalFilters.dateFrom) {
      const timesheetDate = new Date(timesheet.date);
      const filterDate = new Date(approvalFilters.dateFrom);
      if (timesheetDate < filterDate) return false;
    }
    if (approvalFilters.dateTo) {
      const timesheetDate = new Date(timesheet.date);
      const filterDate = new Date(approvalFilters.dateTo);
      if (timesheetDate > filterDate) return false;
    }
    
    // Reference project filter
    if (approvalFilters.referenceProject !== "all") {
      const refId = timesheet.reference_id || timesheet.project_id || timesheet.estimation_id;
      if (refId !== approvalFilters.referenceProject) return false;
    }
    
    return true;
  });

  // Get unique users from pending approvals for filter dropdown
  const uniqueUsersInApprovals = [...new Map(
    pendingApprovals.map(ts => [ts.user_id, { id: ts.user_id, name: getUserName(ts.user_id, ts) }])
  ).values()];

  // Get unique projects/estimations from pending approvals for filter dropdown
  const uniqueReferencesInApprovals = [...new Map(
    pendingApprovals
      .filter(ts => ts.reference_id || ts.project_id || ts.estimation_id)
      .map(ts => {
        const refId = ts.reference_id || ts.project_id || ts.estimation_id;
        const dept = ts.department || "Projects";
        const refNumber = dept === "Projects" ? getProjectNumber(refId) : getEstimationNumber(refId);
        return [refId, { id: refId, number: refNumber }];
      })
  ).values()].filter(ref => ref.number && ref.number !== "Unknown");

  // Clear approval filters
  const clearApprovalFilters = () => {
    setApprovalFilters({
      userId: "all",
      dateFrom: "",
      dateTo: "",
      referenceProject: "all"
    });
  };

  return (
    <Layout user={user}>
      {/*
        -m-8 cancels the Layout's p-8 wrapper so we own full width/height.
        h-screen flex flex-col makes this fill the viewport as a column.
        Header is flex-shrink-0 (never scrolls).
        Content is flex-1 overflow-y-auto (scrolls independently).
      */}
      <Tabs defaultValue="my-timesheets" className="flex flex-col -m-8" style={{height: '100vh'}}>

        {/* ── Pinned header ── */}
        <div className="flex-shrink-0 bg-md3-surface px-8 pt-6 pb-4 border-b border-gray-200 shadow-sm z-20">

          {/* Title + action buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-md3-display-small">Timesheets</h1>
              <p className="text-md3-on-surface-variant mt-1">Track work hours and approvals</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
              <Button onClick={handlePullWorkLogs} disabled={pullLoading} variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full whitespace-nowrap" data-testid="pull-work-logs-btn">
                <Download className="mr-2 h-4 w-4" />
                {pullLoading ? "Pulling..." : "Pull Work Logs"}
              </Button>
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full whitespace-nowrap" onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Timesheet
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-md3-body-small font-medium text-md3-on-surface-variant ">Total Actual Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-md3-headline-large">{getTotalActualHours()}</div>
              </CardContent>
            </Card>
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-md3-body-small font-medium text-md3-on-surface-variant ">Total Earned Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-md3-headline-large text-green-600">{getTotalEarnedHours()}</div>
              </CardContent>
            </Card>
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-md3-body-small font-medium text-md3-on-surface-variant ">Pending Approvals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-md3-headline-large text-yellow-600">{pendingApprovals.length}</div>
              </CardContent>
            </Card>
          </div>

          {/* Tab triggers */}
          <TabsList>
            <TabsTrigger value="my-timesheets">My Timesheets</TabsTrigger>
            {rejectedTimesheets.length > 0 && (
              <TabsTrigger value="rejected" className="text-orange-600">
                <AlertCircle className="mr-1 h-4 w-4" />
                Needs Revision ({rejectedTimesheets.length})
              </TabsTrigger>
            )}
            {pendingApprovals.length > 0 && (
              <TabsTrigger value="approvals">
                Pending Approvals ({pendingApprovals.length})
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        {/* ── End pinned header ── */}

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto px-8 py-6">

          <TabsContent value="my-timesheets">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardContent className="pt-6">
                {loading ? (
                  <p>Loading...</p>
                ) : timesheets.length === 0 ? (
                  <p className="text-md3-on-surface-variant text-center py-8">No timesheets found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table className="bg-md3-surface-container rounded-md3-md">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Billing</TableHead>
                          <TableHead>Dept</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead>Branch</TableHead>
                          <TableHead>Task</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Estimated</TableHead>
                          <TableHead>Actual Hours</TableHead>
                          <TableHead>Earned Hours</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Approved By</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {timesheets.map((timesheet) => {
                          const dept = timesheet.department || "Projects";
                          const refId = timesheet.reference_id || timesheet.project_id || timesheet.estimation_id;
                          
                          return (
                            <TableRow key={timesheet.id}>
                              <TableCell>{format(new Date(timesheet.date), "MMM dd, yyyy")}</TableCell>
                              <TableCell>
                                <Badge 
                                  variant={timesheet.is_billable === false ? "outline" : "default"}
                                  className={timesheet.is_billable === false ? "bg-md3-surface-container text-md3-on-surface-variant" : "bg-blue-600 text-white"}
                                >
                                  {timesheet.is_billable === false ? "Non-Bill" : "Billable"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={dept === "Estimation" ? "secondary" : "default"} className="text-xs">
                                  {dept}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {timesheet.is_billable === false ? (
                                  <span className="text-gray-400 text-md3-body-small">-</span>
                                ) : (
                                  dept === "Projects" 
                                    ? getProjectNumber(refId)
                                    : getEstimationNumber(refId)
                                )}
                              </TableCell>
                              <TableCell>
                              {timesheet.branch ? (
                                <Badge variant="outline">{timesheet.branch}</Badge>
                              ) : (
                                <span className="text-gray-400 text-md3-body-small">-</span>
                              )}
                            </TableCell>
                            <TableCell>{timesheet.task}</TableCell>
                            <TableCell>
                              {timesheet.type ? (
                                <Badge variant="outline">{timesheet.type}</Badge>
                              ) : (
                                <span className="text-gray-400 text-md3-body-small">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-md3-on-surface-variant">{timesheet.estimated_hours || 0}</TableCell>
                            <TableCell className="font-semibold">{timesheet.actual_hours}</TableCell>
                            <TableCell className="font-semibold text-green-600">
                              {timesheet.earned_hours || "-"}
                            </TableCell>
                            <TableCell>{getStatusBadge(timesheet.approval_status)}</TableCell>
                            <TableCell>
                              {timesheet.approved_by_name || (timesheet.approved_by ? getUserName(timesheet.approved_by) : "-")}
                            </TableCell>
                            <TableCell>
                              {timesheet.approval_status === "Draft" && (
                                <div className="flex gap-2 flex-wrap">
                                  {timesheet.work_description && (
                                    <Button size="sm" variant="ghost"
                                      className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                                      title="View work description"
                                      onClick={() => setDescriptionModal({ open: true, text: timesheet.work_description, date: timesheet.week_start_date || timesheet.date || "" })}>
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button size="sm"
                                    variant="outline"
                                    className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                    onClick={() => handleEditTimesheet(timesheet)}>
                                    Edit
                                  </Button>
                                  <Button size="sm"
                                    className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                    onClick={() => handleSubmitTimesheet(timesheet.id)}>
                                    Submit
                                  </Button>
                                  <Button size="sm"
                                    variant="destructive"
                                    className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                    onClick={() => handleDeleteTimesheet(timesheet.id)}>
                                    Delete
                                  </Button>
                                </div>
                              )}
                              {timesheet.approval_status === "Rejected - Resubmit" && (
                                <div className="flex gap-2 flex-wrap">
                                  {timesheet.work_description && (
                                    <Button size="sm" variant="ghost"
                                      className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                                      title="View work description"
                                      onClick={() => setDescriptionModal({ open: true, text: timesheet.work_description, date: timesheet.week_start_date || timesheet.date || "" })}>
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button size="sm"
                                    variant="outline"
                                    className="border-orange-400 text-orange-600 hover:bg-orange-50 transition-all duration-200 rounded-md3-full"
                                    onClick={() => handleEditTimesheet(timesheet)}>
                                    Edit
                                  </Button>
                                  <Button size="sm"
                                    className="bg-orange-500 hover:bg-orange-600 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                    onClick={() => handleSubmitTimesheet(timesheet.id)}>
                                    Resubmit
                                  </Button>
                                </div>
                              )}
                              {!["Draft", "Rejected - Resubmit"].includes(timesheet.approval_status) && (
                                <div className="flex gap-2">
                                  {timesheet.work_description && (
                                    <Button size="sm" variant="ghost"
                                      className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                                      title="View work description"
                                      onClick={() => setDescriptionModal({ open: true, text: timesheet.work_description, date: timesheet.week_start_date || timesheet.date || "" })}>
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {!timesheet.work_description && "-"}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rejected Timesheets Tab - for user to revise and resubmit */}
          <TabsContent value="rejected">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-orange-600 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Timesheets Needing Revision
                </CardTitle>
                <p className="text-md3-body-small text-md3-on-surface-variant">
                  These timesheets were rejected by your manager. Please review the rejection reason, make necessary changes, and resubmit.
                </p>
              </CardHeader>
              <CardContent>
                {rejectedTimesheets.length === 0 ? (
                  <p className="text-md3-on-surface-variant text-center py-8">No rejected timesheets</p>
                ) : (
                  <div className="space-y-4">
                    {rejectedTimesheets.map((timesheet) => (
                      <Card key={timesheet.id} className="border border-orange-200 bg-orange-50">
                        <CardContent className="pt-4">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-4 flex-wrap">
                                <span className="font-semibold">{format(new Date(timesheet.date), "MMM dd, yyyy")}</span>
                                <Badge variant="outline">{timesheet.department}</Badge>
                                <Badge variant="outline">{timesheet.task}</Badge>
                                {timesheet.type && <Badge variant="outline">{timesheet.type}</Badge>}
                                <span className="text-md3-body-small">Actual: <strong>{timesheet.actual_hours}h</strong></span>
                              </div>
                              
                              {/* Reference Info */}
                              <div className="text-md3-body-small text-md3-on-surface-variant">
                                {timesheet.department === "Projects" ? (
                                  <>Project: {projects.find(p => p.id === timesheet.project_id)?.project_number || timesheet.project_id}</>
                                ) : (
                                  <>Estimation: {estimations.find(e => e.id === timesheet.estimation_id)?.estimation_number || timesheet.estimation_id}</>
                                )}
                                {timesheet.branch && <> | Branch: {timesheet.branch}</>}
                              </div>
                              
                              {/* Rejection Reason - Prominent */}
                              <div className="bg-red-100 border border-red-300 rounded-md p-3 mt-2">
                                <p className="text-md3-body-small font-semibold text-red-700 mb-1">Rejection Reason:</p>
                                <p className="text-md3-body-medium text-red-800">{timesheet.rejection_reason || "No reason provided"}</p>
                                {timesheet.approved_by_name && (
                                  <p className="text-md3-body-small text-red-600 mt-1">
                                    Rejected by: {timesheet.approved_by_name} on {timesheet.approved_at ? format(new Date(timesheet.approved_at), "MMM dd, yyyy HH:mm") : "-"}
                                  </p>
                                )}
                              </div>
                              
                              {/* Work Description inline removed — use eye icon in Actions */}
                              
                              {/* Rejection History if multiple rejections */}
                              {timesheet.rejection_history && timesheet.rejection_history.length > 1 && (
                                <details className="mt-2">
                                  <summary className="text-md3-body-small text-orange-700 cursor-pointer">
                                    View rejection history ({timesheet.rejection_history.length} times)
                                  </summary>
                                  <div className="mt-2 space-y-2 pl-4 border-l-2 border-orange-300">
                                    {timesheet.rejection_history.map((history, idx) => (
                                      <div key={idx} className="text-md3-body-small">
                                        <p className="text-gray-600">{history.rejected_at ? format(new Date(history.rejected_at), "MMM dd, yyyy HH:mm") : "-"}</p>
                                        <p className="text-red-700">{history.reason}</p>
                                        <p className="text-gray-500">By: {history.rejected_by_name}</p>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 flex-shrink-0">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleEditTimesheet(timesheet)}
                              >
                                Edit
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-orange-500 hover:bg-orange-600 text-white"
                                onClick={async () => {
                                  try {
                                    await api.patch(`/timesheets/${timesheet.id}/submit`);
                                    toast.success("Timesheet resubmitted for approval");
                                    fetchTimesheets();
                                    fetchRejectedTimesheets();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || "Failed to resubmit");
                                  }
                                }}
                              >
                                Resubmit
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approvals">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardContent className="pt-6">
                {pendingApprovals.length === 0 ? (
                  <p className="text-md3-on-surface-variant text-center py-8">No pending approvals</p>
                ) : (
                  <div className="space-y-4">
                    {/* Filters Section */}
                    <div className="bg-md3-surface-container-high p-4 rounded-lg border border-md3-outline/20">
                      <div className="flex flex-wrap gap-4 items-end">
                        {/* User Filter */}
                        <div className="space-y-1.5 min-w-[180px]">
                          <Label className="text-sm font-medium text-md3-on-surface-variant">User</Label>
                          <Select 
                            value={approvalFilters.userId} 
                            onValueChange={(value) => setApprovalFilters({...approvalFilters, userId: value})}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="All Users" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Users</SelectItem>
                              {uniqueUsersInApprovals.map(user => (
                                <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Date From Filter */}
                        <div className="space-y-1.5 min-w-[150px]">
                          <Label className="text-sm font-medium text-md3-on-surface-variant">Date From</Label>
                          <Input 
                            type="date"
                            value={approvalFilters.dateFrom}
                            onChange={(e) => setApprovalFilters({...approvalFilters, dateFrom: e.target.value})}
                            className="bg-white"
                          />
                        </div>

                        {/* Date To Filter */}
                        <div className="space-y-1.5 min-w-[150px]">
                          <Label className="text-sm font-medium text-md3-on-surface-variant">Date To</Label>
                          <Input 
                            type="date"
                            value={approvalFilters.dateTo}
                            onChange={(e) => setApprovalFilters({...approvalFilters, dateTo: e.target.value})}
                            className="bg-white"
                          />
                        </div>

                        {/* Reference Project Filter */}
                        <div className="space-y-1.5 min-w-[180px]">
                          <Label className="text-sm font-medium text-md3-on-surface-variant">Reference Project #</Label>
                          <Select 
                            value={approvalFilters.referenceProject} 
                            onValueChange={(value) => setApprovalFilters({...approvalFilters, referenceProject: value})}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="All Projects" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Projects</SelectItem>
                              {uniqueReferencesInApprovals.map(ref => (
                                <SelectItem key={ref.id} value={ref.id}>{ref.number}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Clear Filters Button */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={clearApprovalFilters}
                          className="h-10"
                        >
                          Clear Filters
                        </Button>
                      </div>
                      
                      {/* Filter Results Count */}
                      <div className="mt-3 text-sm text-md3-on-surface-variant">
                        Showing {filteredPendingApprovals.length} of {pendingApprovals.length} pending approvals
                      </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      {filteredPendingApprovals.length === 0 ? (
                        <p className="text-md3-on-surface-variant text-center py-8">No approvals match the selected filters</p>
                      ) : (
                        <Table className="bg-md3-surface-container rounded-md3-md">
                          <TableHeader>
                            <TableRow>
                              <TableHead>User</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Billing</TableHead>
                              <TableHead>Dept</TableHead>
                              <TableHead>Reference</TableHead>
                              <TableHead>Branch</TableHead>
                              <TableHead>Task</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Actual Hours</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredPendingApprovals.map((timesheet) => {
                              const dept = timesheet.department || "Projects";
                              const refId = timesheet.reference_id || timesheet.project_id || timesheet.estimation_id;
                              
                              return (
                                <TableRow key={timesheet.id}>
                                  <TableCell className="font-semibold">{getUserName(timesheet.user_id, timesheet)}</TableCell>
                                  <TableCell>{format(new Date(timesheet.date), "MMM dd, yyyy")}</TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant={timesheet.is_billable === false ? "outline" : "default"}
                                      className={timesheet.is_billable === false ? "bg-md3-surface-container text-md3-on-surface-variant" : "bg-blue-600 text-white"}
                                    >
                                      {timesheet.is_billable === false ? "Non-Bill" : "Billable"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={dept === "Estimation" ? "secondary" : "default"}>
                                      {dept}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {timesheet.is_billable === false ? (
                                      <span className="text-gray-400 text-md3-body-small">-</span>
                                    ) : (
                                      dept === "Projects" 
                                        ? getProjectNumber(refId)
                                        : getEstimationNumber(refId)
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {timesheet.branch ? (
                                      <Badge variant="outline">{timesheet.branch}</Badge>
                                    ) : (
                                      <span className="text-gray-400 text-md3-body-small">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>{timesheet.task}</TableCell>
                                  <TableCell>
                                    {timesheet.type ? (
                                      <Badge variant="outline">{timesheet.type}</Badge>
                                    ) : (
                                      <span className="text-gray-400 text-md3-body-small">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="font-semibold">{timesheet.actual_hours}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      {timesheet.work_description && (
                                        <Button size="sm" variant="ghost"
                                          className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                                          title="View work description"
                                          onClick={() => setDescriptionModal({ open: true, text: timesheet.work_description, date: timesheet.week_start_date || timesheet.date || "" })}>
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                      )}
                                      {timesheet.can_approve ? (
                                        <Button size="sm"
                                          className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => openApprovalDialog(timesheet)}>
                                          Review
                                        </Button>
                                      ) : (
                                        <span className="text-gray-400 text-md3-body-small italic">Not your team</span>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        {/* Add Timesheet Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Add Timesheet Entry</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                User: {user?.first_name} {user?.last_name || ''}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Billable / Non-Billable Radio */}
              <div className="border-b pb-4 mb-4">
                <Label className="text-md3-body-medium font-semibold mb-3 block ">Timesheet Type</Label>
                <div className="flex gap-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="is_billable"
                      checked={formData.is_billable === true}
                      onChange={() => setFormData({ 
                        ...formData, 
                        is_billable: true,
                        reference_id: "",
                        project_id: "",
                        type: userDepartment === "Estimation" ? "" : "Base"
                      })}
                      className="w-4 h-4"
                    />
                    <span className="font-medium">Billable</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="is_billable"
                      checked={formData.is_billable === false}
                      onChange={() => setFormData({ 
                        ...formData, 
                        is_billable: false,
                        reference_id: null,
                        project_id: null,
                        estimation_id: null,
                        branch: "",
                        type: null,
                        task: ""
                      })}
                      className="w-4 h-4"
                    />
                    <span className="font-medium">Non-Billable</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-md3-label-large text-gray-700 font-medium">Date *</Label>
                  <DatePicker
                    id="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>

                {/* CONDITIONAL: Show Project/Estimation ONLY if billable */}
                {formData.is_billable && (
                  <>
                    {/* PROJECTS DEPARTMENT */}
                    {userDepartment === "Projects" && (
                      <div className="space-y-2">
                        <Label htmlFor="project_id" className="text-md3-label-large text-gray-700 font-medium">Project *</Label>
                        <Combobox
                          options={projects.map((project) => ({
                            value: project.id,
                            label: `${project.project_number} - ${project.name}`,
                            number: project.project_number,
                            name: project.name,
                          }))}
                          containsSearch
                          testId="timesheet-add-project-picker"
                          value={formData.reference_id}
                          onChange={(value) => {
                            setFormData({ ...formData, reference_id: value, project_id: value, branch: "" });
                          }}
                          placeholder="Select project..."
                          searchPlaceholder="Search projects..."
                          emptyMessage="No projects found."
                          className="bg-white border-gray-300"
                        />
                      </div>
                    )}

                    {/* ESTIMATION DEPARTMENT */}
                    {userDepartment === "Estimation" && (
                      <div className="space-y-2">
                        <Label htmlFor="estimation_id" className="text-md3-label-large text-gray-700 font-medium">Estimation *</Label>
                        <Combobox
                          options={estimations.map((estimation) => ({
                            value: estimation.id,
                            label: `${estimation.estimation_number} - ${estimation.project_name}`,
                            number: estimation.estimation_number,
                            name: estimation.project_name,
                          }))}
                          containsSearch
                          testId="timesheet-add-estimation-picker"
                          value={formData.reference_id}
                          onChange={handleEstimationChange}
                          placeholder="Select estimation..."
                          searchPlaceholder="Search estimations..."
                          emptyMessage="No estimations found."
                          className="bg-white border-gray-300"
                        />
                      </div>
                    )}

                    {/* Branch Selection - Only for multi-branch projects in Projects department */}
                    {userDepartment === "Projects" && formData.reference_id && (() => {
                      const selectedProject = projects.find(p => p.id === formData.reference_id);
                      const isMultiBranch = selectedProject && Array.isArray(selectedProject.branch) && selectedProject.branch.length > 1;
                      
                      if (isMultiBranch) {
                        return (
                          <div className="space-y-2">
                            <Label htmlFor="branch" className="text-md3-label-large text-gray-700 font-medium">Branch *</Label>
                            <Select value={formData.branch} onValueChange={(value) => setFormData({ ...formData, branch: value })}>
                              <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                                <SelectValue placeholder="Select branch" />
                              </SelectTrigger>
                              <SelectContent>
                                {selectedProject.branch.map((branch) => (
                                  <SelectItem key={branch} value={branch}>
                                    {branch}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="task" className="text-md3-label-large text-gray-700 font-medium">Task *</Label>
                  <Select value={formData.task} onValueChange={(value) => setFormData({ ...formData, task: value })}>
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue placeholder="Select task" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Dynamically filter task options based on billable flag */}
                      {(formData.is_billable 
                        ? (userDepartment === "Estimation" ? TASK_OPTIONS_ESTIMATION : BILLABLE_TASKS_PROJECTS)
                        : NON_BILLABLE_TASKS
                      ).map((task) => (
                        <SelectItem key={task} value={task}>
                          {task}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* CONDITIONAL: Show Type field ONLY if billable */}
                {formData.is_billable && (
                  <div className="space-y-2">
                    <Label htmlFor="type" className="text-md3-label-large text-gray-700 font-medium">{userDepartment === "Estimation" ? "Version *" : "Type *"}</Label>
                    {userDepartment === "Estimation" ? (
                      <Select 
                        value={formData.type} 
                        onValueChange={(value) => setFormData({ ...formData, type: value })}
                        disabled={selectedEstimationVersions.length === 0}
                      >
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder={selectedEstimationVersions.length === 0 ? "Select estimation first" : "Select version"} />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedEstimationVersions.map((version) => (
                            <SelectItem key={version} value={version}>
                              {version}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TYPE_OPTIONS_PROJECTS.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="actual_hours" className="text-md3-label-large text-gray-700 font-medium">Actual Hours *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="actual_hours"
                  type="number"
                  step="0.25"
                  placeholder="Enter hours"
                  value={formData.actual_hours}
                  onChange={(e) => setFormData({ ...formData, actual_hours: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="work_description" className="text-md3-label-large text-gray-700 font-medium">Work Completed Description</Label>
                <textarea
                  id="work_description"
                  className="w-full min-h-[80px] px-3 py-2 text-md3-body-small border border-md3-outline rounded-md3-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the work completed (helps manager evaluate earned hours)"
                  value={formData.work_description}
                  onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
                />
              </div>

              <Button type="submit" className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                Create Timesheet Entry
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Review Timesheet</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {selectedTimesheet && `${getUserName(selectedTimesheet.user_id, selectedTimesheet)} - ${format(new Date(selectedTimesheet.date), "MMM dd, yyyy")}`}
              </DialogDescription>
            </DialogHeader>
            {selectedTimesheet && (
              <div className="space-y-6">
                {/* Billing Status Banner */}
                {selectedTimesheet.is_billable === false && (
                  <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 text-gray-600 mr-3" />
                      <div>
                        <p className="font-semibold text-gray-800">Non-Billable Time Entry</p>
                        <p className="text-md3-body-small text-gray-600">Earned hours will be set to 0 upon approval. Manager review is required.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Project Hours Summary - Only for billable entries */}
                {selectedTimesheet.is_billable !== false && projectHoursSummary && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-md3-lg">
                    <h4 className="font-semibold text-blue-900 mb-3">Project Hours Control</h4>

                    {/* Activity-specific breakdown (task being approved) */}
                    {projectHoursSummary.activity_summary && (
                      <div
                        className="mb-4 p-3 bg-indigo-50 rounded border border-indigo-200"
                        data-testid="activity-hours-card"
                      >
                        <div className="flex items-baseline justify-between mb-2">
                          <p className="text-xs font-semibold text-indigo-800">
                            Activity being approved
                          </p>
                          <p className="text-md3-body-small font-medium text-indigo-900">
                            {projectHoursSummary.activity_summary.activity_name || "—"}
                            {projectHoursSummary.activity_summary.activity_type && (
                              <span className="text-xs text-indigo-700 ml-1">
                                ({projectHoursSummary.activity_summary.activity_type})
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="grid grid-cols-4 gap-3 text-md3-body-small">
                          <div>
                            <p className="text-xs text-md3-on-surface-variant">Planned</p>
                            <p className="font-bold text-lg text-indigo-800">
                              {projectHoursSummary.activity_summary.planned_hours}h
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-md3-on-surface-variant">Earned (Approved)</p>
                            <p className="font-bold text-lg text-green-700">
                              {projectHoursSummary.activity_summary.earned_hours}h
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-md3-on-surface-variant">Remaining</p>
                            <p className="font-bold text-lg text-orange-700">
                              {projectHoursSummary.activity_summary.remaining_hours}h
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-md3-on-surface-variant">Usage</p>
                            <p className="font-bold text-lg text-purple-700">
                              {projectHoursSummary.activity_summary.percentage_used.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              projectHoursSummary.activity_summary.percentage_used >= 90
                                ? "bg-red-500"
                                : projectHoursSummary.activity_summary.percentage_used >= 75
                                ? "bg-orange-500"
                                : "bg-indigo-500"
                            }`}
                            style={{
                              width: `${Math.min(
                                projectHoursSummary.activity_summary.percentage_used,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Allocated Hours Breakdown */}
                    <div className="mb-4 p-3 bg-md3-surface-container rounded border border-blue-100">
                      <p className="text-xs font-semibold text-blue-700 mb-2">Allocated Hours Breakdown:</p>
                      <div className="grid grid-cols-4 gap-3 text-md3-body-small">
                        <div>
                          <p className="text-xs text-md3-on-surface-variant">Base Allocated</p>
                          <p className="font-bold text-blue-900">{projectHoursSummary.base_allocated_hours || 0}h</p>
                        </div>
                        <div>
                          <p className="text-xs text-md3-on-surface-variant">Change Orders</p>
                          <p className="font-bold text-green-600">+{projectHoursSummary.co_allocated_hours || 0}h</p>
                        </div>
                        <div className="border-l pl-3 border-blue-200">
                          <p className="text-xs text-md3-on-surface-variant">Total Allocated</p>
                          <p className="font-bold text-xl text-blue-900">{projectHoursSummary.total_allocated_hours || 0}h</p>
                        </div>
                        <div className="border-l pl-3 border-blue-200" data-testid="planned-hours-planning">
                          <p className="text-xs text-md3-on-surface-variant">Planned (Project Planning)</p>
                          <p className="font-bold text-xl text-indigo-700">{projectHoursSummary.planned_hours_from_planning || 0}h</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Hours Usage Grid */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-md3-body-small text-blue-700">Earned Hours Approved</p>
                        <p className="text-md3-headline-large text-green-600">{projectHoursSummary.earned_hours_so_far}</p>
                      </div>
                      <div>
                        <p className="text-md3-body-small text-blue-700">Remaining Hours</p>
                        <p className="text-md3-headline-large text-orange-600">{projectHoursSummary.remaining_hours}</p>
                      </div>
                      <div>
                        <p className="text-md3-body-small text-blue-700">Usage</p>
                        <p className="text-md3-headline-large text-purple-600">{projectHoursSummary.percentage_used.toFixed(1)}%</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full transition-all ${
                            projectHoursSummary.percentage_used >= 90 ? 'bg-red-500' : 
                            projectHoursSummary.percentage_used >= 75 ? 'bg-orange-500' : 
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(projectHoursSummary.percentage_used, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 p-4 bg-md3-surface rounded-md3-lg">
                  {selectedTimesheet.is_billable !== false && (
                    <div>
                      <p className="text-md3-body-small text-md3-on-surface-variant">{(selectedTimesheet.department || "Projects") === "Estimation" ? "Estimation" : "Project"}</p>
                      <p className="font-semibold">{getReferenceName(selectedTimesheet)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Task</p>
                    <p className="font-semibold">{selectedTimesheet.task}</p>
                  </div>
                  {selectedTimesheet.is_billable !== false && (
                    <div>
                      <p className="text-md3-body-small text-md3-on-surface-variant">{(selectedTimesheet.department || "Projects") === "Estimation" ? "Version" : "Type"}</p>
                      <p className="font-semibold">{selectedTimesheet.type}</p>
                    </div>
                  )}
                  {selectedTimesheet.is_billable !== false && (projectHoursSummary?.task_summary || projectHoursSummary?.activity_summary) && (
                    <div data-testid="task-planned-hours">
                      <p className="text-md3-body-small text-md3-on-surface-variant">Planned Hours</p>
                      <p className="font-semibold text-lg text-indigo-700">
                        {projectHoursSummary.task_summary
                          ? projectHoursSummary.task_summary.planned_hours
                          : projectHoursSummary.activity_summary.planned_hours}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Actual Hours</p>
                    <p className="font-semibold text-lg">{selectedTimesheet.actual_hours}</p>
                  </div>
                </div>

                {/* Work Description */}
                {selectedTimesheet.work_description && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md3-lg">
                    <p className="text-md3-body-small font-medium text-blue-900 mb-2">Work Completed Description</p>
                    <p className="text-md3-body-small text-md3-on-surface-variant whitespace-pre-wrap">{selectedTimesheet.work_description}</p>
                  </div>
                )}

                <div className="space-y-2">
                  {selectedTimesheet.is_billable === false ? (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-md3-md">
                      <p className="text-md3-body-small font-semibold text-gray-700">
                        Non-Billable Entry
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Earned hours will be set to 0 automatically upon approval.
                      </p>
                    </div>
                  ) : (
                    <>
                      <Label htmlFor="earned_hours" className="text-md3-label-large text-gray-700 font-medium">Earned Hours (Approved) *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="earned_hours"
                        type="number"
                        step="0.25"
                        placeholder="Enter approved hours"
                        value={earnedHours}
                        onChange={(e) => setEarnedHours(e.target.value)}
                        max={projectHoursSummary?.remaining_hours}
                      />
                      <p className="text-xs text-md3-on-surface-variant">
                        Maximum: {projectHoursSummary?.remaining_hours || 0} hours remaining for this project
                      </p>
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rejection_reason" className="text-md3-label-large text-gray-700 font-medium">Rejection Reason (if rejecting)</Label>
                  <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="rejection_reason"
                    placeholder="Enter reason for rejection"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleApprove}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    onClick={handleReject}
                    variant="destructive"
                    className="flex-1"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Reject and Resubmit
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Timesheet Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold">
                Edit Timesheet Entry {editFormData.is_billable === false && "(Non-Billable)"}
              </DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Make changes to your draft timesheet
              </DialogDescription>
            </DialogHeader>
            
            {/* Non-billable indicator */}
            {editFormData.is_billable === false && (
              <div className="bg-gray-50 border-l-4 border-gray-400 p-3 rounded">
                <p className="text-sm font-medium text-gray-700">Non-Billable Entry</p>
                <p className="text-xs text-gray-500">This entry does not require project/type selection.</p>
              </div>
            )}
            
            <form onSubmit={handleUpdateTimesheet} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_date" className="text-md3-label-large text-gray-700 font-medium">Date *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="edit_date"
                    type="date"
                    value={editFormData.date}
                    onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                    required
                  />
                </div>
                
                {/* Project - Only for billable */}
                {editFormData.is_billable !== false && (
                  <div className="space-y-2">
                    <Label htmlFor="edit_project_id" className="text-md3-label-large text-gray-700 font-medium">Project *</Label>
                    <Combobox
                      options={projects.map((project) => ({
                        value: project.id,
                        label: `${project.project_number} - ${project.name}`
                      }))}
                      value={editFormData.project_id}
                      onChange={(value) => {
                        setEditFormData({ ...editFormData, project_id: value, branch: "" });
                      }}
                      placeholder="Select project..."
                      searchPlaceholder="Search projects..."
                      emptyMessage="No projects found."
                      className="bg-white border-gray-300"
                    />
                  </div>
                )}

                {/* Branch Selection - Only for billable multi-branch projects */}
                {editFormData.is_billable !== false && editFormData.project_id && (() => {
                  const selectedProject = projects.find(p => p.id === editFormData.project_id);
                  const isMultiBranch = selectedProject && Array.isArray(selectedProject.branch) && selectedProject.branch.length > 1;
                  
                  if (isMultiBranch) {
                    return (
                      <div className="space-y-2">
                        <Label htmlFor="edit_branch" className="text-md3-label-large text-gray-700 font-medium">Branch *</Label>
                        <Select value={editFormData.branch} onValueChange={(value) => setEditFormData({ ...editFormData, branch: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select branch" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedProject.branch.map((branch) => (
                              <SelectItem key={branch} value={branch}>
                                {branch}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_task" className="text-md3-label-large text-gray-700 font-medium">Task *</Label>
                  <Select value={editFormData.task} onValueChange={(value) => setEditFormData({ ...editFormData, task: value })}>
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue placeholder="Select task" />
                    </SelectTrigger>
                    <SelectContent>
                      {(editFormData.is_billable === false 
                        ? NON_BILLABLE_TASKS 
                        : ((editFormData.department || "Projects") === "Estimation" ? TASK_OPTIONS_ESTIMATION : TASK_OPTIONS_PROJECTS)
                      ).map((task) => (
                        <SelectItem key={task} value={task}>
                          {task}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Type - Only for billable */}
                {editFormData.is_billable !== false && (
                  <div className="space-y-2">
                    <Label htmlFor="edit_type" className="text-md3-label-large text-gray-700 font-medium">{(editFormData.department || "Projects") === "Estimation" ? "Version *" : "Type *"}</Label>
                    <Select value={editFormData.type} onValueChange={(value) => setEditFormData({ ...editFormData, type: value })}>
                      <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPE_OPTIONS_PROJECTS.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Actual Hours - show in second column for non-billable */}
                {editFormData.is_billable === false && (
                  <div className="space-y-2">
                    <Label htmlFor="edit_actual_hours_nb" className="text-md3-label-large text-gray-700 font-medium">Actual Hours *</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="edit_actual_hours_nb"
                      type="number"
                      step="0.25"
                      placeholder="Enter hours"
                      value={editFormData.actual_hours}
                      onChange={(e) => setEditFormData({ ...editFormData, actual_hours: e.target.value })}
                      required
                    />
                  </div>
                )}
              </div>

              {/* Actual Hours - Only for billable (full width row) */}
              {editFormData.is_billable !== false && (
                <div className="space-y-2">
                  <Label htmlFor="edit_actual_hours" className="text-md3-label-large text-gray-700 font-medium">Actual Hours *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="edit_actual_hours"
                    type="number"
                    step="0.25"
                    placeholder="Enter hours"
                    value={editFormData.actual_hours}
                    onChange={(e) => setEditFormData({ ...editFormData, actual_hours: e.target.value })}
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit_work_description" className="text-md3-label-large text-gray-700 font-medium">Work Completed Description</Label>
                <textarea
                  id="edit_work_description"
                  className="w-full min-h-[80px] px-3 py-2 text-md3-body-small border border-md3-outline rounded-md3-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the work completed (helps manager evaluate earned hours)"
                  value={editFormData.work_description}
                  onChange={(e) => setEditFormData({ ...editFormData, work_description: e.target.value })}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  Update Timesheet
                </Button>
                <Button type="button" variant="outline" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {/* Work Description Viewer Modal */}
      <Dialog open={descriptionModal.open} onOpenChange={(o) => setDescriptionModal(m => ({ ...m, open: o }))}>        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small font-semibold flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              Work Completed Description
              {descriptionModal.date && (
                <span className="text-sm font-normal text-gray-400 ml-1">· {descriptionModal.date}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="px-1 py-2">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto">
              {descriptionModal.text || "No description provided."}
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setDescriptionModal(m => ({ ...m, open: false }))}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pull Work Logs - Conflict Resolution Modal */}
      <Dialog open={conflictModal.open} onOpenChange={(o) => !o && setConflictModal(m => ({ ...m, open: false }))}>
        <DialogContent className="max-w-4xl bg-white" data-testid="pull-conflict-modal">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Resolve Work Log Conflicts
              {conflictModal.period && (
                <span className="text-sm font-normal text-gray-400 ml-1">· {conflictModal.period}</span>
              )}
            </DialogTitle>
            <DialogDescription>
              For each task/date below, a draft timesheet already exists with different hours than your work log. Choose how to resolve each row.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Date</TableHead>
                  <TableHead>Task</TableHead>
                  <TableHead className="w-28 text-right">Draft Hours</TableHead>
                  <TableHead className="w-28 text-right">Log Hours</TableHead>
                  <TableHead className="w-48">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conflictModal.conflicts.map((c) => (
                  <TableRow key={c.log_id} data-testid={`conflict-row-${c.log_id}`}>
                    <TableCell className="font-medium">{c.log_date}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{c.task_description || "(task)"}</div>
                      <div className="text-xs text-gray-500">
                        {c.project_number ? `${c.project_number} · ` : ""}{c.project_name || ""}
                      </div>
                      {c.log_note && (
                        <div className="text-xs text-gray-400 italic mt-1">Note: {c.log_note}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(c.existing_hours).toFixed(2)}
                      <div className="text-[10px] text-gray-400 uppercase">{c.existing_status}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{Number(c.log_hours).toFixed(2)}</TableCell>
                    <TableCell>
                      <Select
                        value={conflictModal.resolutions[c.log_id] || "skip"}
                        onValueChange={(v) => setConflictModal(m => ({ ...m, resolutions: { ...m.resolutions, [c.log_id]: v } }))}
                      >
                        <SelectTrigger className="w-44 bg-white" data-testid={`conflict-action-${c.log_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="skip">Skip (do nothing)</SelectItem>
                          <SelectItem value="keep_existing">Keep draft (mark log pulled)</SelectItem>
                          <SelectItem value="use_log">Overwrite draft with log hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-between items-center pt-2">
            <div className="text-xs text-gray-500">
              <span className="font-medium">Skip</span> leaves both untouched ·{" "}
              <span className="font-medium">Keep draft</span> keeps your manual edit and won't pull again ·{" "}
              <span className="font-medium">Overwrite</span> only works on Draft / Rejected rows.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConflictModal(m => ({ ...m, open: false }))} data-testid="conflict-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleResolveConflicts}
                disabled={pullLoading}
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary"
                data-testid="conflict-apply-btn"
              >
                {pullLoading ? "Applying..." : "Apply Resolutions"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      </Tabs>
    </Layout>
  );
}
